import { useCallback, useState, type ReactNode } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { MarkdownReact } from "@/components/MarkdownReact";
import { RichTextEditor } from "@/components/RichTextEditor";

/**
 * Props for {@link RichTextEditorSheet}.
 */
export interface RichTextEditorSheetProps {
  /** Whether the panel is open. */
  open: boolean;
  /** Called when the open state changes. */
  onOpenChange: (open: boolean) => void;
  /** Title shown in the panel header. */
  title: string;
  /** The markdown content to edit. */
  value: string;
  /** Called with the updated markdown whenever the user edits the content. */
  onChange: (value: string) => void;
  /** Placeholder shown while the editor is empty. */
  placeholder?: string;
  /** When true, renders the content as read-only markdown instead of an editor. */
  readOnly?: boolean;
  /** Footer actions. Defaults to a single Done button that closes the panel. */
  footer?: ReactNode;
  /** Optional content shown directly below the editor. */
  note?: ReactNode;
}

/**
 * Editing panel for a markdown field, wrapping {@link RichTextEditor} in the
 * header/editor/footer shell every quote text field uses.
 *
 * Opens as a side sheet and can be popped out into a centered modal for a
 * larger editing surface; closing the panel collapses it back to a sheet.
 * Switching between the two remounts the editor, so the panel is only suitable
 * for content owned by the caller and passed through `value` — the editor holds
 * no state of its own to lose.
 *
 * @param props - The panel props
 * @returns The editor panel, as a sheet or a modal depending on its state
 */
export function RichTextEditorSheet({
  open,
  onOpenChange,
  title,
  value,
  onChange,
  placeholder,
  readOnly = false,
  footer,
  note,
}: RichTextEditorSheetProps) {
  const [expanded, setExpanded] = useState(false);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setExpanded(false);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const body = (
    <div className="mt-4 flex min-h-0 flex-1 flex-col">
      {readOnly ? (
        <div className="min-h-0 flex-1 overflow-y-auto text-sm prose prose-sm dark:prose-invert max-w-none">
          <MarkdownReact>{value}</MarkdownReact>
        </div>
      ) : (
        <RichTextEditor
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="flex-1"
        />
      )}
      {note}
      <div className="mt-4 flex shrink-0 justify-end gap-2">
        {footer ?? (
          <Button onClick={() => handleOpenChange(false)}>Done</Button>
        )}
      </div>
    </div>
  );

  if (expanded) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        {/*
         * DialogContent is `w-full`, which would run the modal edge to edge on
         * any viewport narrower than the cap. Sized off the viewport instead, so
         * it always keeps a margin and only reaches 1024px when there is room.
         */}
        <DialogContent className="flex h-[85vh] w-[calc(100vw-4rem)] max-w-5xl flex-col gap-0 overflow-hidden">
          <DialogHeader className="shrink-0">
            {/* Padded clear of the close button DialogContent renders at right-4. */}
            <div className="flex items-center justify-between gap-2 pr-8">
              <DialogTitle>{title}</DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setExpanded(false)}
              >
                <Minimize2 className="h-4 w-4" />
                <span className="sr-only">Collapse editor to a side panel</span>
              </Button>
            </div>
          </DialogHeader>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {/*
       * 768px gives the editor ~717px of text width once the sheet's border and
       * padding come out — within a few px of the 720px (PAGE_CONTENT_WIDTH)
       * that this content renders at inside the quote page margins, and enough
       * for the ~672px toolbar to sit on one row.
       */}
      <SheetContent className="flex w-full flex-col overflow-hidden sm:w-[768px] sm:max-w-[768px]">
        <SheetHeader className="shrink-0">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle>{title}</SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setExpanded(true)}
            >
              <Maximize2 className="h-4 w-4" />
              <span className="sr-only">Expand editor to a larger window</span>
            </Button>
          </div>
        </SheetHeader>
        {body}
      </SheetContent>
    </Sheet>
  );
}
